import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { Avatar, Box, Button, useTheme } from '@ublend-npm/aulaui-core';
import { cleanState } from '@ublend-npm/aula-editor';
import type { LtiProvider } from '@ublend-npm/aula-schema';
import useCreateComment from '@core/comments/hooks/useCreateComment';
import { CommentEntityType } from '@core/utils/commentsEntityTypes';
import uuid from '@core/utils/uuid';
import { API_URL, FILE_URL } from '@core/constants/endpoints';
import { COMMENT_ITEM_TYPE } from '@core/constants/analytics';
import isEmptyContent from '@app/components/presentational/ClassRoom/Feed/isEmptyContent';
import useEnabledLTIIntegrations from '@app/hooks/useEnabledLTIIntegrations';
import { getInitials } from '@app/utils/user';
import { createStyles } from './ResponsiveCommentEditor.styles';

type ResponsiveCommentEditorProps = {
  entityId: string;
  entityType: CommentEntityType;
  parent?: string;
  root?: string;
  onCancel?: () => void;
  onSuccess?: () => void;
  autoFocus?: boolean;
  isCreatingComment: boolean;
  EditorComponent: any;
  user: any;
  institution: string;
  accessToken: string;
  spaceId: string;
  ltiProviders?: Array<LtiProvider>;
  isEducator: boolean;
};

const isAulaEditorPortalTarget = (target: EventTarget | null): boolean =>
  target instanceof Element && Boolean(target.closest('.portal'));

const ResponsiveCommentEditor = ({
  entityId,
  entityType,
  parent,
  root,
  onCancel,
  onSuccess,
  autoFocus = false,
  isCreatingComment,
  EditorComponent,
  user,
  institution,
  accessToken,
  spaceId,
  ltiProviders,
  isEducator,
}: ResponsiveCommentEditorProps) => {
  const styles = createStyles(useTheme());
  const editorRef = useRef();
  const rootRef = useRef<HTMLElement | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [editorState, setEditorState] = useState(cleanState);
  const [editorKey, setEditorKey] = useState(uuid());
  const [canSubmit, setCanSubmit] = useState(true);
  const [isEditorExpanded, setIsEditorExpanded] = useState(autoFocus);

  const resetEditor = useCallback(() => {
    setEditorState(cleanState);
    setEditorKey(uuid());
    setIsEditorExpanded(autoFocus);
  }, [autoFocus]);

  const { mutate: createComment } = useCreateComment(
    entityType,
    entityId,
    () => {
      resetEditor();
      if (onSuccess) onSuccess();
    }
  );

  const editorConfig = useMemo(
    () => ({
      filesUrl: FILE_URL(),
      apiUrl: API_URL(),
      accessToken,
      spaceId,
      institution,
      userId: user?.objectId,
      ltiProviders,
      itemType: COMMENT_ITEM_TYPE,
      isEducator,
    }),
    [
      accessToken,
      institution,
      isEducator,
      ltiProviders,
      spaceId,
      user?.objectId,
    ]
  );
  const filteredEditorConfig = useEnabledLTIIntegrations(editorConfig);
  const loading = isCreatingComment || isSubmitting;
  const userName =
    `${user?.firstName || ''} ${user?.lastName || ''}`.trim() || 'You';
  const userInitials = getInitials({
    firstName: user?.firstName,
    lastName: user?.lastName,
  });

  const handleFilesStatusChange = useCallback(
    ({ isUpdating, isPersistable, hasError }: any) => {
      setCanSubmit(isPersistable && !isUpdating && !hasError);
    },
    []
  );

  const handleCancel = () => {
    if (loading) return;
    resetEditor();
    if (onCancel) onCancel();
  };

  const handleCreateComment = () => {
    if (isEmptyContent(editorState) || !canSubmit || loading) return;

    createComment({
      content: JSON.stringify(editorState),
      parent,
      root,
      entityId,
      entityType,
    });
  };

  useEffect(() => {
    const handleOutsidePointerDown = (event: PointerEvent) => {
      const { target } = event;
      if (
        !isEditorExpanded ||
        !(target instanceof Node) ||
        rootRef.current?.contains(target) ||
        isAulaEditorPortalTarget(target) ||
        !isEmptyContent(editorState)
      ) {
        return;
      }

      setIsEditorExpanded(false);
    };

    document.addEventListener('pointerdown', handleOutsidePointerDown, true);
    return () => {
      document.removeEventListener(
        'pointerdown',
        handleOutsidePointerDown,
        true
      );
    };
  }, [editorState, isEditorExpanded]);

  const handleEditorBlur = (event: React.FocusEvent<HTMLDivElement>) => {
    const nextTarget = event.relatedTarget;
    if (
      nextTarget instanceof Node &&
      (event.currentTarget.contains(nextTarget) ||
        isAulaEditorPortalTarget(nextTarget))
    ) {
      return;
    }

    // Aula Editor deliberately blurs Slate while opening or moving between
    // block-menu items. During those transitions the browser reports body (or
    // no element) as the next target even though the editor interaction is
    // still active. A real pointer interaction outside is handled above.
    if (!nextTarget || nextTarget === document.body) return;

    // Keep drafts and their actions visible until they are submitted or
    // explicitly cancelled. Only an empty editor should collapse on blur.
    if (!isEmptyContent(editorState)) return;

    setIsEditorExpanded(false);
  };

  return (
    <Box
      ref={rootRef}
      component="section"
      aria-label={parent ? 'Add a reply' : 'Add a comment'}
      sx={styles.root}
    >
      <Avatar
        altText={userName}
        fallbackText={userInitials}
        kind="circular"
        src={user?.avatar}
        sx={styles.avatar}
      />
      <Box
        data-expanded={isEditorExpanded}
        data-testid="responsive-comment-editor"
        onClick={() => setIsEditorExpanded(true)}
        onBlurCapture={handleEditorBlur}
        onFocusCapture={() => setIsEditorExpanded(true)}
        onPointerDownCapture={() => {
          setIsEditorExpanded(true);
        }}
        sx={styles.editorCard}
      >
        <EditorComponent
          key={editorKey}
          ref={editorRef}
          config={filteredEditorConfig}
          editorState={editorState}
          placeholder={parent ? 'Write a reply...' : 'Write a comment...'}
          onChange={setEditorState}
          isSubmitting={loading}
          setSubmittingState={setIsSubmitting}
          readOnly={isCreatingComment}
          type="comment"
          autoFocus={autoFocus}
          onFilesStatusChange={handleFilesStatusChange}
          isAssignmentsEnabled
        />
        {isEditorExpanded && (
          <Box sx={styles.actions}>
            <Button
              kind="secondary"
              size="small"
              type="button"
              onClick={(event) => {
                event.stopPropagation();
                handleCancel();
              }}
              disabled={(!onCancel && isEmptyContent(editorState)) || loading}
              sx={styles.actionButton}
            >
              Cancel
            </Button>
            <Button
              kind="primary"
              size="small"
              type="button"
              onClick={handleCreateComment}
              disabled={isEmptyContent(editorState) || !canSubmit || loading}
              loading={isCreatingComment}
              sx={styles.actionButton}
            >
              {parent ? 'Reply' : 'Comment'}
            </Button>
          </Box>
        )}
      </Box>
    </Box>
  );
};

export default ResponsiveCommentEditor;
